import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CompanyAttributeGuard, ExternalUrlRedirectGuard, FeatureGuard } from '@app/guards';
import { PermissionGuard } from '@app/guards/permission.guard';
import { RoutesWithOptions } from '@app/interfaces';
import { BenefitsResolver } from '@benefits/benefits.resolver';
import {
    BenefitsUpsellView,
    ChangeRequestIndexView,
    ChangeRequestShowView,
    EmployeeIndexView,
    EmployeeStoreView,
    LoadingRedirectView,
    PayrollBenefitStoreView,
    PlanIndexView,
    PlanShowView,
    PlanUpdateView,
} from '@benefits/views';
import { environment } from '@env/environment';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { EmployeePortalRedirectView } from './views/employee-portal-redirect.view';
import { SettingsIndexView } from '@benefits/views/settings/index/settings-index.view';
import { BenefitTypeResolver, PayrollPendingStateResolver } from '@payroll/resolvers';
import { FeatureFlag } from '@app/enums';
import { BenefitInvoiceStoreView } from '@benefits/views/benefit-invoice/store/benefit-invoice-store.view';
import { BenefitInvoiceShowView } from '@benefits/views/benefit-invoice/show/benefit-invoice-show.view';

const routes: RoutesWithOptions = [
    {
        path: '',
        redirectTo: 'plans',
        pathMatch: 'full',
    },
    {
        path: 'plans',
        canActivate: [PermissionGuard],
        data: {
            permission: [...BenefitPlan.permission.view],
        },
        children: [
            {
                path: '',
                component: PlanIndexView,
            },
            {
                path: ':benefitPlan',
                children: [
                    {
                        path: '',
                        component: PlanShowView,
                        resolve: {
                            models: BenefitsResolver,
                        },
                    },
                    {
                        path: 'benefits',
                        children: [
                            {
                                path: '',
                                component: PlanUpdateView,
                            },
                            {
                                path: ':benefit',
                                children: [
                                    {
                                        path: '',
                                        component: PlanUpdateView,
                                        resolve: {
                                            models: BenefitsResolver,
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'employees',
                        children: [
                            {
                                path: '',
                                component: EmployeeIndexView,
                                resolve: {
                                    models: BenefitsResolver,
                                },
                            },
                            {
                                path: 'new',
                                component: EmployeeStoreView,
                                resolve: {
                                    models: BenefitsResolver,
                                },
                            },
                        ],
                    },
                    {
                        path: 'change-requests',
                        children: [
                            {
                                path: '',
                                component: ChangeRequestIndexView,
                                canActivate: [PermissionGuard],
                                data: {
                                    permission: ['administrate'],
                                },
                                resolve: {
                                    models: BenefitsResolver,
                                },
                            },
                            {
                                path: ':benefitChangeRequest',
                                component: ChangeRequestShowView,
                                canActivate: [PermissionGuard],
                                data: {
                                    permission: ['administrate'],
                                },
                                resolve: {
                                    models: BenefitsResolver,
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: 'settings',
        canActivate: [PermissionGuard, FeatureGuard, CompanyAttributeGuard],
        data: {
            permission: [...BenefitPlan.permission.edit],
            feature: { flags: [FeatureFlag.benefitsPayroll] },
            companyAttributeName: 'isBenefitsClient',
        },
        children: [
            {
                path: '',
                component: SettingsIndexView,
                resolve: {
                    isPayrollPending: PayrollPendingStateResolver,
                },
                data: {
                    safeResolve: true,
                },
            },
            {
                path: 'connect-benefit',
                component: PayrollBenefitStoreView,
                canActivate: [PermissionGuard],
                resolve: {
                    isPayrollPending: PayrollPendingStateResolver,
                    benefitTypes: BenefitTypeResolver,
                },
                data: {
                    safeResolve: true,
                    permission: ['accessPayroll'],
                },
            },
            {
                path: 'upload-invoice',
                component: BenefitInvoiceStoreView,
                canActivate: [PermissionGuard],
                data: {
                    permission: ['accessPayroll'],
                },
            },
            {
                path: 'benefit-invoice/:benefitInvoice',
                component: BenefitInvoiceShowView,
                canActivate: [PermissionGuard],
                resolve: {
                    isPayrollPending: PayrollPendingStateResolver,
                    models: BenefitsResolver,
                },
                data: {
                    safeResolve: true,
                    permission: ['accessPayroll'],
                },
            },
        ],
    },
    {
        path: 'info',
        component: BenefitsUpsellView,
    },
    {
        //SB Admin Portal
        path: 'admin',
        component: LoadingRedirectView,
        canActivate: [PermissionGuard, ExternalUrlRedirectGuard],
        data: {
            permission: ['administrate'],
            externalUrl: environment.simplyBenefitsAdminPanelUrl,
        },
    },
    {
        //SB Employee Portal
        path: 'portal',
        component: EmployeePortalRedirectView,
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class BenefitsRouterModule {}
